import type { GraphEdgeSettingsId } from '../graphSettings';
import type { GraphStpTechnology } from '../graphTechnologies';
import type { NodeId } from '../nodes/types';
import type { Packet } from '../pathLookup/Packet';
import type { ReportSeverity } from '../report';
import type { Branded } from '../types/Branded';
import type { EdgeLabels } from './EdgeLabels';
import type { EdgePositions } from './EdgePositions';
import type { EdgeStyle } from './EdgeStyle';
import type { ProtocolEdgeType } from './constants';

export type EdgeId = Branded<string, 'EdgeId'>;
export type EdgeDirection = 'directed' | 'undirected' | 'bidirected';
export type LoopId = Branded<number, 'LoopId'>;

export type EdgeLabelsOptions = {
  wrapCenterLabel: boolean;
  wrapLinecapLabel: boolean;
};

export interface EdgeData {
  id: EdgeId;
  circle?: boolean;
  direction: EdgeDirection;
  edgeSettingsId: GraphEdgeSettingsId;
  intentCheckResult?: ReportSeverity | null;
  labels?: EdgeLabels;
  labelsOptions?: EdgeLabelsOptions;
  positions: EdgePositions | null;
  protocol?: ProtocolEdgeType;
  shift: number;
  source: NodeId;
  style?: EdgeStyle;
  target: NodeId;
}

export type EdgeRecord = Record<EdgeId, EdgeData>;

export type RenderableEdgeData = EdgeData & {
  positions: NonNullable<EdgeData['positions']>;
};

export function isRenderableEdgeData(
  edgeData: EdgeData,
): edgeData is RenderableEdgeData {
  return edgeData.positions !== null;
}

export type EdgeRelatedTechnology = GraphStpTechnology;

// Topology edge

export interface TopologyEdgeData extends EdgeData {
  children: EdgeId[];
  relatedTechnology?: EdgeRelatedTechnology;
}

export const isTopologyEdgeData = (
  edgeData: EdgeData,
): edgeData is TopologyEdgeData => 'children' in edgeData;

// Path-lookup edge

export interface TrafficScore {
  accepted: number;
  dropped: number;
  forwarded: number;
  total: number;
}

export interface PathLookupEdgeData extends EdgeData {
  loopIds?: LoopId[];
  nextEdgeIds: EdgeId[];
  packet: Packet;
  prevEdgeIds: EdgeId[];
  severityInfo: Record<ReportSeverity, number>;
  sourceIfaceName?: string;
  targetIfaceName?: string;
  trafficScore: TrafficScore;
}

export const isPathLookupEdgeData = (
  edgeData?: EdgeData | null,
): edgeData is PathLookupEdgeData =>
  edgeData !== undefined && edgeData !== null && 'packet' in edgeData;

export type UnpositionedEdgeData<T extends EdgeData> = Omit<T, 'positions'>;
export type UnpositionedTopologyEdgeData =
  UnpositionedEdgeData<TopologyEdgeData>;
export type UnpositionedPathLookupEdgeData =
  UnpositionedEdgeData<PathLookupEdgeData>;
